// LAP-548 / LAP-550 — Fiche détail d'une valeur unique (clic sur une ligne
// d'une page détail Marché). Route générique :
//   /investments/market/:slug/asset/:assetId              (marchés sans sous-vue)
//   /investments/market/:slug/:sub/asset/:assetId         (marchés à sous-vues)
// Données : /api/investment/asset-detail/?market=&sub=&id= → { asset, news }.
import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Stack, Button, CircularProgress, Link as MuiLink } from '@mui/material'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { makeGetRequest } from 'utils/api'
import DetailPageLayout from '../../_shared/DetailPageLayout'
import Delta from '../../_shared/Delta'
import { invTheme } from '../../_shared/theme'
import MARKET_CONFIG from './marketConfig'
import AddToPortfolioDialog, { assetName, assetPrice } from './AddToPortfolioDialog'
import AddToTrackedDialog from './AddToTrackedDialog'

// ── Helpers de formatage (alignés sur marketConfig.js) ────────────────────
const compactNumber = (v) => {
  const n = parseFloat(v)
  if (isNaN(n)) return v
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)} Mds`
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)} M`
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)} k`
  return n.toLocaleString('fr-FR')
}
const plainNumber = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? v : n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })
}

// Champs jamais affichés dans la grille (repris dans l'en-tête ou internes).
const HIDDEN_FIELDS = new Set([
  'id', 'name', 'symbol', 'price', 'exchange_rate', 'image', 'flag',
  'description', 'card', 'completude', 'isin', 'type_obj',
  'base_currency', 'target_currency',
])
// Champs en grands nombres → format compact.
const COMPACT_FIELDS = new Set([
  'marketcap', 'marketCap', 'volume', 'avgVolume', 'valorisation_actuelle',
  'montant_leve', 'nombre_dutilisateurs',
])
// Champs en pourcentage → suffixe « % ».
const PCT_FIELDS = new Set([
  'variation_1d', 'variation_1_jour', 'variantion_1_jour', 'change_percentage',
  'changePercentage', 'variation_1_an', 'variation_24h', 'variation_1y',
  'rendement', 'coupon', 'tendance_1_an', 'croissance_ca_1_an', 'vacance',
  'fiscalite', 'taux_dinteret', 'effort_depargne', 'ecart_plus_bas', 'ecart_plus_haut',
])
// Libellés FR par champ (sinon on humanise le nom technique).
const LABELS = {
  sector: 'Secteur', industry: 'Industrie', exchange: 'Bourse', currency: 'Devise',
  per: 'PER', marketcap: 'Capitalisation', marketCap: 'Capitalisation', volume: 'Volume',
  avgVolume: 'Volume moyen', variation_1d: 'Variation 1J', variation_1_jour: 'Variation 1J',
  variantion_1_jour: 'Variation 1J', change_percentage: 'Variation %',
  changePercentage: 'Variation 24h', variation_1_an: 'Variation 1A',
  variation_24h: 'Variation 24h', variation_1y: 'Variation 1A',
  plus_bas_1_an: 'Plus bas 1A', plus_haut_1_an: 'Plus haut 1A',
  ecart_plus_bas: 'Écart plus bas', ecart_plus_haut: 'Écart plus haut',
  avg50: 'Moyenne 50j', avg200: 'Moyenne 200j', priceAvg50: 'Moyenne 50j',
  priceAvg200: 'Moyenne 200j', yearLow: 'Plus bas 1A', yearHigh: 'Plus haut 1A',
  last_annual_dividend: 'Dividende annuel', life_note_fondamentaux: 'Note fondamentaux',
  life_note_technique: 'Note technique', score: 'Score', country: 'Pays', ceo: 'Dirigeant',
  website: 'Site web', sous_type: 'Sous-type', emetteur: 'Émetteur', notation: 'Notation',
  echeance: 'Échéance', duration: 'Duration', type: 'Type', callable: 'Remboursable',
  puttable: 'Cessible', converible: 'Convertible', localisation: 'Localisation',
  prix_m2: 'Prix/m²', superficie_m2: 'Surface (m²)', vacance: 'Vacance', cashflow: 'Cashflow',
  tendance_1_an: 'Tendance 1A', recommandation: 'Recommandation', loyer_mensuel: 'Loyer mensuel',
  charges_mensuelles: 'Charges mensuelles', fiscalite: 'Fiscalité', financement: 'Financement',
  taux_dinteret: "Taux d'intérêt", duree_du_credit: 'Durée du crédit',
  apport_personnel: 'Apport personnel', effort_depargne: "Effort d'épargne",
  tradeMonth: 'Mois de cotation', annee: 'Année', subtype: 'Sous-type', marque: 'Marque',
  modele: 'Modèle', anneeProduction: 'Année de production', etat: 'État', rarete: 'Rareté',
  artiste: 'Artiste', numero_deserie: 'N° de série', boite_et_papiers: 'Boîte & papiers',
  mouvement: 'Mouvement', materiau: 'Matériau', diametre: 'Diamètre',
  indice_dumarche_des_montres: 'Indice marché montres', investor: 'Investisseurs',
  recommendation: 'Recommandation', annee_decreateion: 'Année de création',
  phase_dedeveloppement: 'Phase de développement', valorisation_actuelle: 'Valorisation',
  montant_leve: 'Montant levé', croissance_ca_1_an: 'Croissance CA 1A',
  rentabilite_actuelle: 'Rentable', cashflow_net: 'Cashflow net', marche_cible: 'Marché cible',
  equipe_fondatrice: 'Équipe fondatrice', indice_detraction: 'Indice de traction',
  nombre_dutilisateurs: "Nombre d'utilisateurs", business_model: 'Business model',
  brevet_ou_propriete_intellectuelle: 'Brevet / PI', variation_percent: 'Variation',
}

const humanize = (k) =>
  k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

const formatValue = (key, value) => {
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non'
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number' || (!isNaN(parseFloat(value)) && isFinite(value))) {
    if (COMPACT_FIELDS.has(key)) return compactNumber(value)
    if (PCT_FIELDS.has(key)) return `${plainNumber(value)} %`
    return plainNumber(value)
  }
  return String(value)
}

// Premier champ numérique exploitable parmi une liste de candidats.
const firstNum = (a = {}, keys = []) => {
  for (const k of keys) {
    const v = a[k]
    const n = parseFloat(v)
    if (v !== undefined && v !== null && v !== '' && !isNaN(n)) return n
  }
  return null
}

// Variation principale affichée dans l'en-tête, selon ce que l'actif fournit.
const headerVariation = (a = {}) =>
  firstNum(a, [
    'variation_1d', 'variation_1_jour', 'variantion_1_jour', 'changePercentage',
    'variation_24h', 'variation_1_an', 'tendance_1_an', 'variation_percent',
  ])

// Indicateurs mis en avant (bande KPI sous le prix). On garde les 4 premiers
// réellement renseignés pour l'actif courant.
const KPI_DEFS = [
  { label: 'Variation', keys: ['variation_1d', 'variation_1_jour', 'variantion_1_jour', 'changePercentage', 'variation_24h', 'variation_percent'], kind: 'delta' },
  { label: 'Variation 1A', keys: ['variation_1_an', 'tendance_1_an'], kind: 'delta' },
  { label: 'Capitalisation', keys: ['marketcap', 'marketCap', 'valorisation_actuelle'], kind: 'compact' },
  { label: 'Volume', keys: ['volume'], kind: 'compact' },
  { label: 'Rendement', keys: ['rendement', 'coupon'], kind: 'pct' },
  { label: 'PER', keys: ['per'], kind: 'plain' },
  { label: 'Score', keys: ['score', 'life_note_fondamentaux'], kind: 'plain' },
  { label: 'Montant levé', keys: ['montant_leve'], kind: 'compact' },
]

const buildKpis = (a) => {
  if (!a) return []
  const out = []
  for (const def of KPI_DEFS) {
    if (out.length >= 4) break
    const v = firstNum(a, def.keys)
    if (v === null) continue
    out.push({ label: def.label, kind: def.kind, value: v })
  }
  return out
}

// Repères de cours réels (pas d'historique inventé) → mini-graphique en barres.
const LEVEL_DEFS = [
  { keys: ['plus_bas_1_an', 'yearLow'], label: 'Plus bas 1A' },
  { keys: ['avg200', 'priceAvg200'], label: 'Moy. 200j' },
  { keys: ['avg50', 'priceAvg50'], label: 'Moy. 50j' },
  { keys: ['price'], label: 'Prix actuel', current: true },
  { keys: ['plus_haut_1_an', 'yearHigh'], label: 'Plus haut 1A' },
]

const buildLevelsChart = (a, accent) => {
  if (!a) return null
  const pts = LEVEL_DEFS.map((d) => ({
    label: d.label,
    value: firstNum(a, d.keys),
    current: !!d.current,
  })).filter((p) => p.value !== null)
  // Sous 2 repères, un graphique n'apporte rien → on n'en affiche pas.
  if (pts.length < 2) return null
  return {
    data: {
      labels: pts.map((p) => p.label),
      datasets: [
        {
          label: 'Niveau',
          data: pts.map((p) => p.value),
          backgroundColor: pts.map((p) =>
            p.current ? accent || '#63a0ff' : 'rgba(99,160,255,0.32)',
          ),
          borderRadius: 6,
          maxBarThickness: 56,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: { display: false },
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => plainNumber(ctx.parsed.y),
          },
        },
      },
      scales: {
        x: {
          ticks: { color: 'rgba(255,255,255,0.6)', font: { size: 11 } },
          grid: { display: false },
        },
        y: {
          ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 11 } },
          grid: { color: 'rgba(255,255,255,0.06)' },
        },
      },
    },
  }
}

export default function AssetDetail() {
  const { t } = useTranslation('layout')
  const { slug, sub, assetId } = useParams()
  const navigate = useNavigate()

  const config = MARKET_CONFIG[slug] || MARKET_CONFIG.financial
  const hasSubviews = Array.isArray(config.subviews) && config.subviews.length > 0
  const currentSub = hasSubviews
    ? config.subviews.find((s) => s.value === sub) || config.subviews[0]
    : null
  const dataType = hasSubviews ? currentSub?.dataType || '' : ''
  const backTo = hasSubviews
    ? `/investments/market/${slug}/${currentSub?.value || ''}`
    : `/investments/market/${slug}`

  const [asset, setAsset] = useState(null)
  const [news, setNews] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [buyOpen, setBuyOpen] = useState(false)
  const [trackOpen, setTrackOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      setNotFound(false)
      const { data, status } = await makeGetRequest(
        `/api/investment/asset-detail/?market=${config.sourceType}&sub=${dataType}&id=${assetId}`,
        {},
        true,
      )
      if (cancelled) return
      if (status === 200 && data && data.asset) {
        setAsset(data.asset)
        setNews(Array.isArray(data.news) ? data.news : [])
      } else {
        setNotFound(true)
      }
      setLoading(false)
    }
    run()
    return () => {
      cancelled = true
    }
  }, [config.sourceType, dataType, assetId])

  const figures = useMemo(() => {
    if (!asset) return []
    return Object.keys(asset)
      .filter((k) => !HIDDEN_FIELDS.has(k))
      .map((k) => ({ key: k, label: LABELS[k] || humanize(k), value: formatValue(k, asset[k]) }))
      .filter((f) => f.value !== null && f.value !== undefined && f.value !== '')
  }, [asset])

  const variation = asset ? headerVariation(asset) : null
  const price = asset ? assetPrice(asset) : 0
  const kpis = useMemo(() => buildKpis(asset), [asset])
  const levelsChart = useMemo(() => buildLevelsChart(asset, config.accent), [asset, config.accent])
  // Nom de la valeur en grand titre ; le marché + le symbole deviennent l'eyebrow.
  const valueName = asset ? assetName(asset) : t('investments.detail.assetLoading', 'Chargement…')
  const title = t(config.title)
  const subtitle = asset && asset.symbol ? asset.symbol : ''

  const renderKpiValue = (k) => {
    if (k.kind === 'delta') return <Delta value={k.value} unit="%" />
    if (k.kind === 'compact') return compactNumber(k.value)
    if (k.kind === 'pct') return `${plainNumber(k.value)} %`
    return plainNumber(k.value)
  }

  // Corps de la fiche : en-tête prix + grille de chiffres clés + description + actus.
  const body = (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : notFound || !asset ? (
        <Box sx={{ py: 6, textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
          {t('investments.detail.assetNotFound', 'Valeur introuvable.')}
        </Box>
      ) : (
        <>
          {/* En-tête prix / variation */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box>
              <Box sx={{ ...invTheme.subtitle, color: 'rgba(255,255,255,0.55)' }}>
                {t('investments.detail.currentPrice', 'Prix actuel')}
              </Box>
              <Box sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>
                {plainNumber(price)}
                {asset.currency ? ` ${asset.currency}` : ''}
              </Box>
            </Box>
            {variation !== null && (
              <Box sx={{ pb: 0.5 }}>
                <Delta value={variation} unit="%" />
              </Box>
            )}
          </Box>

          {/* Bande d'indicateurs clés mis en avant */}
          {kpis.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${kpis.length}, minmax(0, 1fr))`,
                gap: 1.5,
                mb: 3,
              }}
            >
              {kpis.map((k) => (
                <Box
                  key={k.label}
                  sx={{
                    p: 1.5,
                    borderRadius: invTheme.cardRadius,
                    border: invTheme.cardBorder,
                    background: 'linear-gradient(180deg, rgba(99,160,255,0.10), rgba(255,255,255,0.02))',
                  }}
                >
                  <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>{k.label}</Box>
                  <Box sx={{ fontSize: 18, fontWeight: 700 }}>{renderKpiValue(k)}</Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Grille de chiffres clés */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 1.5,
              mb: 3,
            }}
          >
            {figures.map((f) => (
              <Box
                key={f.key}
                sx={{
                  p: 1.5,
                  borderRadius: invTheme.cardRadius,
                  border: invTheme.cardBorder,
                  background: 'rgba(255,255,255,0.03)',
                }}
              >
                <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', mb: 0.5 }}>{f.label}</Box>
                <Box sx={{ fontSize: 15, fontWeight: 600 }}>{f.value}</Box>
              </Box>
            ))}
          </Box>

          {/* Description */}
          {asset.description ? (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ ...invTheme.title, mb: 1 }}>
                {t('investments.detail.about', 'À propos')}
              </Box>
              <Box sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                {asset.description}
              </Box>
            </Box>
          ) : null}

          {/* Actualités liées */}
          <Box>
            <Box sx={{ ...invTheme.title, mb: 1 }}>
              {t('investments.detail.relatedNews', 'Actualités liées')}
            </Box>
            {news.length === 0 ? (
              <Box sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                {t('investments.detail.noNews', 'Aucune actualité disponible.')}
              </Box>
            ) : (
              <Stack spacing={1}>
                {news.map((n) => (
                  <Box
                    key={n.id}
                    sx={{
                      p: 1.5,
                      borderRadius: invTheme.cardRadius,
                      border: invTheme.cardBorder,
                      background: 'rgba(255,255,255,0.03)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ fontSize: 14, fontWeight: 600, flexGrow: 1 }}>{n.title}</Box>
                      {n.url ? (
                        <MuiLink
                          href={n.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: config.accent, display: 'inline-flex' }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </MuiLink>
                      ) : null}
                    </Box>
                    {n.summary ? (
                      <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                        {n.summary}
                      </Box>
                    ) : null}
                    <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
                      {[n.source, n.published_at ? new Date(n.published_at).toLocaleDateString('fr-FR') : '']
                        .filter(Boolean)
                        .join(' · ')}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Box>
  )

  return (
    <>
      <DetailPageLayout
        pageTitleOverride={valueName}
        title={title}
        subtitle={subtitle}
        icon={config.icon}
        accent={config.accent}
        backTo={backTo}
        chart={
          levelsChart ? (
            <Bar data={levelsChart.data} options={levelsChart.options} />
          ) : null
        }
        tableTitle={t('investments.detail.assetSheet', 'Fiche de la valeur')}
        tableContent={body}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="contained"
              disabled={!asset}
              onClick={() => setBuyOpen(true)}
              startIcon={<ShoppingCartOutlinedIcon />}
              sx={{
                backgroundColor: config.accent,
                color: '#001b42',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: config.accent, filter: 'brightness(1.08)' },
                '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)' },
              }}
            >
              {t('investments.detail.addToPortfolio', 'Ajouter aux investissements')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!asset}
              onClick={() => setTrackOpen(true)}
              startIcon={<TrackChangesOutlinedIcon />}
              sx={{
                color: 'rgba(255,255,255,0.85)',
                borderColor: 'rgba(255,255,255,0.25)',
                textTransform: 'none',
                '&:hover': { borderColor: config.accent },
              }}
            >
              {t('investments.detail.addToTracked', 'Ajouter au suivi')}
            </Button>
          </Stack>
        }
      />
      <AddToPortfolioDialog
        open={buyOpen}
        assets={asset ? [asset] : []}
        onClose={() => setBuyOpen(false)}
        onAdded={() => navigate(backTo)}
      />
      <AddToTrackedDialog
        open={trackOpen}
        asset={asset}
        onClose={() => setTrackOpen(false)}
      />
    </>
  )
}
